import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { HubPerformance } from '@/hooks/useGestorPerformance';

interface CapacityUtilizationChartProps {
  data: HubPerformance[];
}

const CapacityUtilizationChart: React.FC<CapacityUtilizationChartProps> = ({ data }) => {
  // Ordenar por utilização decrescente (mostrar todos os hubs)
  const sortedData = [...data].sort((a, b) => b.utilizacao_capacidade - a.utilizacao_capacidade);
  
  const chartData = sortedData.map((hub) => ({
    name: hub.hub_name,
    utilizacao: hub.utilizacao_capacidade,
    capacidade: hub.capacity,
    demanda: hub.total_obras,
    profissionais: hub.total_profissionais
  }));

  const getBarColor = (utilizacao: number) => {
    if (utilizacao >= 100) return 'hsl(var(--destructive))';
    if (utilizacao >= 80) return 'hsl(var(--warning))';
    if (utilizacao >= 50) return 'hsl(var(--success))';
    return 'hsl(var(--info))';
  };

  // Altura dinâmica: 40px por hub, mínimo 180px
  const chartHeight = Math.max(chartData.length * 40, 180);
  const containerHeight = Math.min(chartHeight, 280);
  const needsScroll = chartHeight > containerHeight;

  // Determinar domínio máximo do eixo X
  const maxUtilization = Math.max(...chartData.map(d => d.utilizacao), 100);
  const xDomain = Math.ceil(maxUtilization / 20) * 20; // Arredondar para múltiplo de 20

  return (
    <Card>
      <CardHeader className="py-3 pb-2">
        <CardTitle className="text-sm font-medium">Utilização de Capacidade</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {chartData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum dado disponível
          </div>
        ) : (
          <ScrollArea className={needsScroll ? `h-[${containerHeight}px]` : ''} style={{ height: containerHeight }}>
            <div style={{ height: chartHeight, minHeight: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={chartData} 
                  layout="vertical" 
                  margin={{ left: 0, right: 30, top: 5, bottom: 5 }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    horizontal={true} 
                    vertical={false} 
                    stroke="hsl(var(--border))" 
                  />
                  <XAxis 
                    type="number" 
                    domain={[0, xDomain]} 
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} 
                    tickLine={false} 
                    axisLine={false}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={140}
                    tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--chart-tooltip-bg))',
                      border: '1px solid hsl(var(--chart-tooltip-border))',
                      borderRadius: 'var(--chart-tooltip-radius)',
                      fontSize: '12px',
                      boxShadow: 'var(--chart-tooltip-shadow)',
                    }}
                    formatter={(value: number, name: string, props: any) => {
                      return [
                        `${value}% (Demanda: ${props.payload.demanda} / Capacidade: ${props.payload.capacidade})`,
                        'Utilização'
                      ];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <ReferenceLine 
                    x={100} 
                    stroke="hsl(var(--destructive))" 
                    strokeDasharray="5 5" 
                    strokeOpacity={0.8}
                  />
                  <ReferenceLine 
                    x={80} 
                    stroke="hsl(var(--warning))" 
                    strokeDasharray="3 3" 
                    strokeOpacity={0.5} 
                  />
                  <Bar 
                    dataKey="utilizacao" 
                    radius={[0, 4, 4, 0]} 
                    maxBarSize={24}
                    minPointSize={0}
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.utilizacao === 0 ? 'transparent' : getBarColor(entry.utilizacao)} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ScrollArea>
        )}
        <div className="flex items-center justify-center gap-3 mt-3 pt-2 border-t border-border/50 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-info"></div>
            <span>Subutilizado</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-success"></div>
            <span>Adequado</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-warning"></div>
            <span>Alto</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-destructive"></div>
            <span>Excedido</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CapacityUtilizationChart;
